import { Avatar } from '@creditornot/cb-avatar';
import { IconButton } from '@creditornot/cb-button';
import { Moon, MoreHorizontal, Sun } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { User } from '@creditornot/wolt-auth';
import { useAuthActions } from '@creditornot/wolt-auth/react';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { ConfirmModal, LanguageSelector } from 'common/components';
import { useThemeMode } from 'common/themeMode';
import { openIntercom } from 'modules/intercom';

type Props = {
  className?: string;
  user: User | undefined;
};

const MenuRoot = styled.div`
  min-width: 180px;
`;

const StyledMenuItemGroup = styled(MenuItemGroup)`
  padding: 12px 16px;
  &:not(:first-child) {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const LanguageSelectorContainer = styled(MenuItemGroup)`
  padding: 12px 16px;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const UserTitle = styled.h3`
  font-size: 15px;
  font-weight: 500;
  color: ${cbSemanticCssVars.textPrimary};
  margin-block-end: 0;
`;

const UserDropdown = ({ className, user }: Props) => {
  const { logout } = useAuthActions();
  const { themeMode, setThemeMode } = useThemeMode();
  const history = useHistory();

  const [show, setShow] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = () => {
    setIsLoggingOut(true);
    logout();
    history.push('/');
  };

  const userInitials = useMemo(
    () => (user?.name?.first_name || '').charAt(0) + (user?.name?.last_name || '').charAt(0),
    [user],
  );

  return (
    <>
      <Popover
        className={className}
        content={
          <MenuRoot>
            {user && (
              <StyledMenuItemGroup>
                <UserTitle>{user.email || ''}</UserTitle>
              </StyledMenuItemGroup>
            )}
            <LanguageSelectorContainer title={<FormattedMessage id="navigation.languages" />}>
              <LanguageSelector onLanguageSelect={() => setShow(false)} maxHeight={270} />
            </LanguageSelectorContainer>
            <StyledMenuItemGroup>
              <MenuActionItem
                variant="black"
                icon={themeMode === 'light' ? <Moon /> : <Sun />}
                onClick={() => setThemeMode(themeMode === 'dark' ? 'light' : 'dark')}
              >
                <FormattedMessage id={themeMode === 'light' ? 'navigation.switch-dark' : 'navigation.switch-light'} />
              </MenuActionItem>
              {user && (
                <>
                  <MenuActionItem variant="black" onClick={openIntercom}>
                    <FormattedMessage id="navigation.support" />
                  </MenuActionItem>
                  <MenuActionItem variant="black" onClick={() => setIsLogoutModalOpen(true)}>
                    <FormattedMessage id="navigation.logout" />
                  </MenuActionItem>
                </>
              )}
            </StyledMenuItemGroup>
          </MenuRoot>
        }
        onClickOutside={() => setShow(false)}
        show={show}
        renderArrow={false}
        disableContentPadding
      >
        {user ? (
          <Avatar
            onClick={() => setShow(prev => !prev)}
            size="small"
            imgSrc={user.profile_picture?.url}
            initials={userInitials}
          />
        ) : (
          <IconButton onClick={() => setShow(prev => !prev)} size="small" variant="grey" background="staticWhite">
            <MoreHorizontal />
          </IconButton>
        )}
      </Popover>
      <ConfirmModal
        header={<FormattedMessage id="logout.header" />}
        description={<FormattedMessage id="logout.description" values={{ user: user?.email }} />}
        cancelText={<FormattedMessage id="logout.cancel" />}
        confirmText={<FormattedMessage id="logout.sign-out" />}
        onCancel={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
        open={isLogoutModalOpen}
        loading={isLoggingOut}
      />
    </>
  );
};

export default UserDropdown;
